import { Collapse, Skeleton } from '@mui/material';
import Tooltip from '@root/components/v2/ui-elements/Tooltip';
import { ChevronDown, LayoutGrid } from 'lucide-react';
import { useId, useState } from 'react';
import './style.scss';

// Vertical tab menu of the side overlays, first built for the Inventory resource DetailsPanel.
// `categories` is [{ title?, tabs: [{ key, label, icon?, hasCount?, count?, badge?, disabled?, disabledTooltip? }] }].
// A category without a title renders its items with no collapsible header, for overlays with only a few tabs.

// Count in brackets after the label, in full with thousands separators, e.g. "(1,230)".
const TabCount = ({ count, isLoading }) => {
  if (isLoading) return <Skeleton width="18px" />;
  // No count means it could not be fetched, so the tab shows none.
  if (count === undefined || count === null) return null;
  return <span className="SideOverlayTabMenu__count">&nbsp;({Math.round(count).toLocaleString('en-US')})</span>;
};

const MenuCategory = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  const itemsId = useId();
  if (!title) return <div className="SideOverlayTabMenu__items">{children}</div>;
  return (
    <div>
      <button
        type="button"
        data-testid={`click-side-overlay-menu-category-${title}`}
        data-clickable="true"
        className="SideOverlayTabMenu__categoryHeader"
        aria-expanded={open}
        aria-controls={itemsId}
        onClick={() => setOpen(prev => !prev)}>
        {title}
        <ChevronDown size={14} style={{ transform: open ? 'none' : 'rotate(-90deg)' }} />
      </button>
      <Collapse in={open}>
        <div id={itemsId} className="SideOverlayTabMenu__items">
          {children}
        </div>
      </Collapse>
    </div>
  );
};

const MenuItem = ({ item, isActive, isCountLoading, onChange }) => {
  const Icon = item.icon ?? LayoutGrid;
  const label = item.label ?? item.key;
  const button = (
    <button
      type="button"
      data-testid={`click-side-overlay-menu-tab-${item.key}`}
      data-clickable="true"
      className={`SideOverlayTabMenu__item ${isActive ? 'SideOverlayTabMenu__item--active' : ''}`}
      aria-current={isActive ? 'page' : undefined}
      disabled={item.disabled}
      onClick={() => onChange(item.key)}>
      <Icon size={14} />
      {/* A long label is cut short before its count; the full label shows on hover. */}
      <Tooltip title={label}>
        <span className="SingleLine">{label}</span>
      </Tooltip>
      {item.hasCount && <TabCount count={item.count} isLoading={isCountLoading} />}
      {item.badge}
    </button>
  );
  if (!item.disabledTooltip) return button;
  // A disabled button fires no mouse events, so the tooltip hangs off a wrapper.
  return (
    <Tooltip title={item.disabled ? item.disabledTooltip : ''} isIcon={true}>
      <span>{button}</span>
    </Tooltip>
  );
};

const MenuSkeleton = ({ grouped }) =>
  Array.from({ length: grouped ? 4 : 1 }).map((_, i) => (
    <div key={i}>
      {grouped && (
        <div className="SideOverlayTabMenu__skeletonHeader">
          <Skeleton width="60px" />
        </div>
      )}
      <div className="SideOverlayTabMenu__items">
        {Array.from({ length: 3 }).map((__, j) => (
          <Skeleton
            key={j}
            variant="rectangular"
            width="100%"
            className="SideOverlayTabMenu__skeletonItem"
            sx={{ borderRadius: '16px' }}
          />
        ))}
      </div>
    </div>
  ));

// Up/Down/Home/End move focus between the enabled items, skipping those inside collapsed groups (MUI Collapse
// keeps them mounted but hidden).
const handleMenuKeyDown = event => {
  const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
  if (!keys.includes(event.key) || !event.target.classList.contains('SideOverlayTabMenu__item')) return;
  const items = [...event.currentTarget.querySelectorAll('.SideOverlayTabMenu__item:not(:disabled)')].filter(
    item => !item.closest('.MuiCollapse-hidden'),
  );
  const index = items.indexOf(event.target);
  const next = {
    ArrowDown: items[(index + 1) % items.length],
    ArrowUp: items[(index - 1 + items.length) % items.length],
    Home: items[0],
    End: items[items.length - 1],
  }[event.key];
  if (!next) return;
  event.preventDefault();
  next.focus();
};

// `grouped` only shapes the loading skeleton, since categories are usually not known yet while loading.
const SideOverlayTabMenu = ({
  categories,
  activeTab,
  onChange,
  isLoading = false,
  isCountLoading = false,
  grouped = true,
  ariaLabel = 'Details sections',
}) => (
  <nav
    className="SideOverlayTabMenu CustomScrollbar CustomScrollbar-Small"
    aria-label={ariaLabel}
    onKeyDown={handleMenuKeyDown}>
    {isLoading ? (
      <MenuSkeleton grouped={grouped} />
    ) : (
      categories.map((category, index) => (
        <MenuCategory key={category.title ?? index} title={category.title}>
          {category.tabs.map(item => (
            <MenuItem
              key={item.key}
              item={item}
              isActive={item.key === activeTab}
              isCountLoading={isCountLoading}
              onChange={onChange}
            />
          ))}
        </MenuCategory>
      ))
    )}
  </nav>
);

// Lays the menu out beside the tab content, which takes the remaining width.
export const SideOverlayBody = ({ menu, children, className = '' }) => (
  <div className={`SideOverlayBody ${className}`}>
    {menu}
    {children}
  </div>
);

export default SideOverlayTabMenu;
